import { Formik, useField } from 'formik';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { Button, Text, Alert, OTPInput, Strong } from '@summer/ui-components';

import SupportContact from 'components/shared/SupportContact/SupportContact';
import { Link } from 'components/shared/typography';
import { usePlatform } from 'hooks/platform';
import { isLoading } from 'redux/selectors/ui.selectors';
import { getUserIsVerified } from 'redux/selectors/user.selectors';
import { verifySmsFactorSchema } from 'schemas/onboard';

import { MfaPageContainer } from './shared';

const VerifySmsFactor = ({
  phoneNumber,
  isLoading,
  isSending,
  smsVerifyError,
  smsOTPSendingError,
  status,
  onSendCode,
  onSubmit,
  handleChangeNumber,
  showCreateAccountHeader = false,
  canChangeNumber = true,
}) => {
  const OTP_INPUT_TYPE = 'number';
  const { isMobile } = usePlatform();
  const [isCodeSent, setIsCodeSent] = useState(false);
  const handleSave = ({ code }) => {
    setIsCodeSent(false);
    onSubmit(code);
  };

  const handleSend = () => {
    setIsCodeSent(true);
    onSendCode();
  };

  const CustomOtpInput = ({ ...props }) => {
    const fieldValues = useField(props);
    const field = fieldValues[0];
    const helpers = fieldValues[2];

    const handleChangeInput = value => {
      helpers.setValue(value);
    };

    return (
      <OTPInput
        dataTestId="sms-otp-input"
        numInputs={6}
        value={field.value}
        onChange={handleChangeInput}
        width={isMobile ? 40 : 55}
        height={isMobile ? 40 : 55}
        inputType={OTP_INPUT_TYPE}
      />
    );
  };

  const phoneLastFour = phoneNumber.slice(-4);
  return (
    <MfaPageContainer
      status={status}
      showCreateAccountHeader={showCreateAccountHeader}
      header="Verify your phone number to continue"
    >
      <TextContainer>
        <Text paragraph center data-testid="phone-confirmation-text">
          Enter the code sent to <Strong>XXX-XXX-{phoneLastFour}</Strong>.
          {canChangeNumber && (
            <>
              Wrong&nbsp;number?{' '}
              <Link
                testId="edit-sms-phone"
                onClick={handleChangeNumber}
                disabled={isSending}
              >
                Change
              </Link>
            </>
          )}
        </Text>
      </TextContainer>
      <Formik
        onSubmit={handleSave}
        initialValues={{
          code: '',
        }}
        validationSchema={verifySmsFactorSchema}
      >
        {({ handleSubmit, isValid }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <CustomOtpInput name="code" />

              <ButtonContainer>
                <Button type="submit" disabled={!isValid} isLoading={isLoading}>
                  Verify
                </Button>
              </ButtonContainer>
            </Form>
          );
        }}
      </Formik>
      <StyledLink data-testid="link-Resend" onClick={() => handleSend()}>
        Resend Code
      </StyledLink>
      {/* TODO: Add more granular error handling as part of MFA v1 */}
      {isCodeSent && !smsOTPSendingError && (
        <AlertContainer>
          <Alert theme="success" showIcon={true} dismissable={false}>
            <Text small paragraph data-testid="sent-confirmation-text">
              New code sent, you should receive a text
            </Text>
          </Alert>
        </AlertContainer>
      )}
      {smsVerifyError && (
        <AlertContainer>
          <Alert className="server-error" theme="warning" dismissable={false}>
            Sorry, that code is incorrect.
          </Alert>
        </AlertContainer>
      )}
      {smsOTPSendingError && (
        <AlertContainer>
          <Alert className="sending-error" theme="warning" dismissable={false}>
            Sorry, a new code could not be sent.
          </Alert>
        </AlertContainer>
      )}
      <SupportContact />
    </MfaPageContainer>
  );
};

const TextContainer = styled.div`
  margin-bottom: 24px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const Form = styled.form`
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  justify-items: center;
`;

const ButtonContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const StyledLink = styled(Link)`
  margin-bottom: 16px;
`;

const AlertContainer = styled.div`
  width: 100%;
  margin-top: 0px;
  margin-bottom: 16px;
  max-width: 360px;
`;

const mapStateToProps = state => ({
  isLoading: isLoading(state, 'verifySmsFactor'),
  isSending: isLoading(state, 'sendSmsFactorOTP'),
  emailIsVerified: getUserIsVerified(state),
});

export default connect(mapStateToProps)(VerifySmsFactor);
